import { observer } from 'mobx-react-lite';
import { createNewText, addTaskButtonText } from './QuickAdd.locstring.json';
import loc from 'owa-localize';
/* tslint:disable:jsx-no-lambda WI:47647 */
import * as React from 'react';
import type QuickAddViewState from '../schema/QuickAddViewState';
import { ActionButton } from '@fluentui/react/lib/Button';
import { ControlIcons } from 'owa-control-icons';
import { Icon } from '@fluentui/react/lib/Icon';
import { ITextField, TextField } from '@fluentui/react/lib/TextField';
import type { KeyboardEvent } from 'react';
import { KeyCodes } from '@fluentui/react/lib/Utilities';
import { getDensityModeString } from 'owa-session-store';
import { isFeatureEnabled } from 'owa-feature-flags';

const styles = require('./QuickAdd.scss');
const classNames = require('classnames/bind').bind(styles);
const MAX_SUBJECT_LENGTH = 255;

export interface QuickAddProps {
    uiState: QuickAddViewState;
    onAddClicked: () => void;
    onSubjectChange: (
        ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
        newValue: string
    ) => void;
    onFocus: () => void;
    onBlur: () => void;

    // Optional param to override default "Add a task" title
    quickAddTitle?: string;

    // Optional param to add styling
    className?: string;
}

export default observer(function QuickAdd(props: QuickAddProps) {
    const textInput = React.useRef<ITextField>();
    const forwardTextInputRef = ref => (textInput.current = ref);
    const addTask = () => {
        // Move focus back into the text field no matter how the task was added
        // e.g. clicking on right button
        if (textInput.current) {
            textInput.current.focus();
            props.onAddClicked();
        }
    };
    const onPlusButtonClick = () => {
        if (textInput.current) {
            textInput.current.focus();
            props.onAddClicked();
        }
    };
    const subjectOnKeyDown = (target: KeyboardEvent<EventTarget>) => {
        switch (target.keyCode) {
            case KeyCodes.enter:
                target.preventDefault();
                target.stopPropagation();
                props.onAddClicked();
                break;
            case KeyCodes.escape:
                const { uiState } = props;
                if (uiState.subject.trim().length > 0) {
                    target.preventDefault();
                    target.stopPropagation();
                }
                break;
        }
    };
    const { uiState, onSubjectChange, onFocus, onBlur, quickAddTitle, className } = props;
    if (!uiState.isShown) {
        return null;
    }
    const rightButtonShown: boolean = uiState.isFocused || uiState.subject.trim().length > 0;
    const rightButtonDisabled: boolean = uiState.subject.trim().length == 0;
    const addTitle = quickAddTitle ? quickAddTitle : loc(createNewText);
    const istodoListDensityEnabled = isFeatureEnabled('mon-todoList-density');
    return (
        <div
            className={classNames(
                styles.addTask,
                istodoListDensityEnabled && getDensityModeString(),
                {
                    highlightNoText: uiState.isFocused && rightButtonDisabled,
                    highlight: !rightButtonDisabled,
                },
                className
            )}>
            <div
                onClick={() => {
                    onPlusButtonClick();
                }}
                title={addTitle}
                className={styles.leftIconPlusContainer}>
                <Icon
                    iconName={ControlIcons.Add}
                    className={classNames(styles.leftIconPlus, {
                        disabled: rightButtonShown && rightButtonDisabled,
                    })}
                />
            </div>

            <TextField
                componentRef={forwardTextInputRef}
                className={styles.addTaskWrapper}
                styles={{ field: styles.addTaskInput }}
                onChange={onSubjectChange}
                borderless={true}
                value={uiState.subject}
                maxLength={MAX_SUBJECT_LENGTH}
                autoFocus={false}
                onFocus={onFocus}
                onBlur={onBlur}
                onKeyDown={subjectOnKeyDown}
                placeholder={addTitle}
                ariaLabel={addTitle}
            />
            {rightButtonShown && (
                <ActionButton
                    className={styles.rightButton}
                    disabled={rightButtonDisabled}
                    text={loc(addTaskButtonText)}
                    styles={{
                        rootDisabled: styles.rightButtonDisabled,
                        label: styles.rightButtonLabel,
                        flexContainer: styles.buttonFlexContainer,
                    }}
                    onClick={addTask}
                />
            )}
        </div>
    );
});
